import { ControlBase, getFromDataPath } from "aic";
import { useEffect, useRef, useState } from "react";
import { Button, Collapse, Form } from "react-bootstrap";
import { ArrowLeftRight, ChevronDown, ChevronRight, DashCircle, Pen } from "react-bootstrap-icons";
import { classMerge } from "../../utility/classMerge";
import { ConditionComparatorEnum } from "../ars_ldm";
import { PATCH_ARS_STATE, REMOVE_WHERE_CLAUSE, useARSContext } from "../contexts";
import { ARSSelect } from "../inputs/ARSSelect";
import { ARSText } from "../inputs/ARSText";
import { ARSVariable } from "../inputs/ARSVariable";
import { isSubClause } from "../typeGuards/isSubClause";
import { isSubClauseWithCondition } from "../typeGuards/isSubClauseWithCondition";
import { isWhereClauseCondition } from "../typeGuards/isWhereClauseCondition";
import { formatConditionAsCode } from "../utility/formatConditionAsCode";
import { parseConditionFromCode } from "../utility/parseConditionFromCode";
import styles from "./WhereClauseConditionItemControl.module.css";

/**
 * Props for the WhereClauseConditionItemControl component.
 */
export interface WhereClauseConditionItemControlProps extends React.HTMLAttributes<HTMLDivElement> {
  /** RFC 6901 JSON Pointer path to the WhereClauseCondition in ARSContext state */
  dataPath: string;
  /** If this where clause is part of a compound expression and can be removed */
  isRemovable?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A control for editing a single WhereClauseCondition item, representing an inline condition
 * in the format: [dataset].[variable] [comparator] [value(s)].
 *
 * This component displays and manages the four fields of a where clause condition:
 * - dataset + variable: Combined selector using ARSVariable with dual paths
 * - comparator: The comparison operator (EQ, NE, GT, GE, LT, LE, IN, NOTIN)
 * - value: The value(s) for comparison (string array, displayed as newline-separated text via ARSText)
 *
 * The component starts in compact mode showing a code representation (e.g., "ADSL.SAFFL = 'Y'")
 * that can be clicked to expand to the full editor. The compact/expanded state is managed internally.
 *
 * Inline editing mode allows users to directly edit the code representation with real-time validation.
 *
 * All changes are synchronized with ARSContext state using the PATCH_ARS_STATE action.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the WhereClauseCondition in ARSContext state.
 * @param props.isRemovable - Indicates if this condition can be removed (if part of a compound expression).
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the where clause condition editor.
 */
export const WhereClauseConditionItemControl = ({
  dataPath,
  isRemovable = false,
  disabled = false,
  ...rest
}: WhereClauseConditionItemControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const cancelRef = useRef(false);
  const subClausePath = dataPath.endsWith("/condition")
    ? dataPath.substring(0, dataPath.lastIndexOf("/condition"))
    : // istanbul ignore next
      "";
  const subClause = getFromDataPath(subClausePath, state, isSubClauseWithCondition);
  const canSwapToReference = subClause !== undefined;

  // Get the condition from state
  const whereClauseCondition = getFromDataPath(dataPath, state, isWhereClauseCondition);
  const thisId =
    getFromDataPath(dataPath.substring(0, dataPath.lastIndexOf("/")), state, isSubClause)?.id ?? "";
  const groupComparator =
    whereClauseCondition?.comparator === ConditionComparatorEnum.IN ||
    whereClauseCondition?.comparator === ConditionComparatorEnum.NOTIN;

  // Convert comparators enum to options for ARSSelect
  const comparatorOptions = state.comparators.map((comp) => ({
    value: comp,
    label: comp,
  }));

  // Compact mode with toggle (default view)
  const compactCode = formatConditionAsCode(whereClauseCondition, state.variables);

  // Get available variables for validation
  const availableVariables = state.variables.map((v) => v.value);

  // Validate current edit value
  const parsedCondition =
    isEditing && editValue ? parseConditionFromCode(editValue, availableVariables) : undefined;
  const isValid = parsedCondition !== false;
  const isInvalid = isEditing && editValue.length > 0 && !isValid;

  // Focus effect - focus input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const input = document.getElementById(`${dataPath.replace(/\//g, "-")}-edit-input`);
      /* istanbul ignore else -- Input element should always be present in edit mode */
      if (input) {
        input.focus();
      }
    }
  }, [isEditing, dataPath]);

  /**
   * Handles entering edit mode.
   */
  const handleStartEdit = (): void => {
    setEditValue(compactCode);
    setIsEditing(true);
    cancelRef.current = false;
  };

  /**
   * Saves the changes to the condition.
   *
   * @param value - The value to save
   */
  const saveChanges = (value: string): void => {
    const parsed = parseConditionFromCode(value, availableVariables);
    if (parsed) {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: parsed.condition,
      });
    }
    setIsEditing(false);
  };

  /**
   * Handles key down events in edit mode.
   *
   * @param e - Keyboard event.
   */
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    /* istanbul ignore else -- Handler only processes keys in edit mode */
    if (isEditing) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancelRef.current = true;
        /* istanbul ignore else -- Event target should always be an HTMLElement */
        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        /* istanbul ignore else -- Event target should always be an HTMLElement */
        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      }
    }
  };

  /**
   * Handles the blur event for the input field.
   * Saves changes if the blur was not caused by a cancel action.
   *
   * @param e - The focus event from the blur.
   */
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    if (cancelRef.current) {
      // Canceling - revert to original value
      setEditValue(compactCode);
      cancelRef.current = false;
      setIsEditing(false);
    } else if (isEditing) {
      // Get the actual input value from the event target to avoid stale state closures
      const inputElement = e.target as HTMLInputElement;
      saveChanges(inputElement.value);
    }
  };

  /**
   * Handles removing a where clause.
   * Reverts to single condition if only 2 clauses remain.
   */
  const handleRemoveClause = (): void => {
    const parentPath = dataPath.substring(
      0,
      dataPath.lastIndexOf("/compoundExpression/whereClauses/"),
    );
    dispatch({
      operation: REMOVE_WHERE_CLAUSE,
      dataPath: parentPath,
      id: thisId,
    });
  };

  /**
   * Handles swapping a condition clause to a referenced entity.
   */
  const handleSwapToReference = (): void => {
    /* istanbul ignore else */
    if (subClause && subClausePath) {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: subClausePath,
        value: {
          id: subClause.id,
          level: subClause.level,
          order: subClause.order,
          subClauseId: "-- None --",
        },
      });
    }
  };

  return (
    <div {...rest}>
      <div
        className={styles.compactContainer}
        onKeyDownCapture={handleKeyDown}
        onBlur={handleBlur}
      >
        <Button
          id={`${dataPath.replace(/\//g, "-")}-toggle-expand`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
          variant="link"
          size="sm"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          disabled={isEditing || disabled}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Button>
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          {!isEditing ? (
            <div
              className="form-control form-control-sm"
              style={{ border: "none" }}
            >
              <code
                className={styles.codeBlock}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {compactCode || <span className="text-muted">No condition defined</span>}
              </code>
            </div>
          ) : (
            <>
              <ControlBase
                id={`${dataPath.replace(/\//g, "-")}-edit-input`}
                aria-label="Enter condition"
                disabled={disabled}
                autoFocus={isEditing}
                value={editValue}
                whiteSpaceFix={true}
                setValue={setEditValue}
                spellCheck={false}
                className={classMerge([
                  isEditing && isValid && editValue.length > 0
                    ? "is-valid"
                    : isInvalid
                      ? "is-invalid"
                      : "",
                ])}
              />
            </>
          )}
        </div>
        <div className={styles.actionButtons}>
          {!isEditing && (
            <>
              <ArrowLeftRight
                id={`${dataPath.replace(/\//g, "-")}-swap`}
                className={canSwapToReference ? "text-primary" : "text-muted"}
                role="button"
                aria-label="Swap to reference"
                aria-disabled={!canSwapToReference || disabled}
                onClick={canSwapToReference && !disabled ? handleSwapToReference : undefined}
                style={{ cursor: canSwapToReference && !disabled ? "pointer" : "not-allowed" }}
              />
              <Pen
                id={`${dataPath.replace(/\//g, "-")}-edit`}
                className={classMerge([`text-primary`])}
                role="button"
                aria-label="Edit condition"
                aria-disabled={disabled}
                onClick={disabled ? undefined : handleStartEdit}
              />
              {isRemovable && (
                <DashCircle
                  id={`${dataPath.replace(/\//g, "-")}-remove`}
                  className="text-danger"
                  role="button"
                  aria-label={`Remove clause`}
                  aria-disabled={disabled}
                  onClick={disabled ? undefined : () => handleRemoveClause()}
                  style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Form.Text
        style={{
          height: isEditing ? "1.5em" : 0,
          minHeight: isEditing ? "1.5em" : 0,
          transition: "height 0.2s ease, min-height 0.2s ease",
        }}
        className={classMerge([
          isValid && editValue.length > 0
            ? "valid-feedback"
            : isInvalid
              ? "invalid-feedback"
              : "text-muted",
          styles.validationText,
        ])}
      >
        {isValid && editValue.length > 0
          ? "Valid condition"
          : isInvalid
            ? "Invalid condition format"
            : ""}
      </Form.Text>
      <Collapse in={isExpanded}>
        <div>
          <ARSVariable
            datasetDataPath={`${dataPath}/dataset`}
            variableDataPath={`${dataPath}/variable`}
            id={`${dataPath.replace(/\//g, "-")}-variable`}
            label="Variable"
            placeHolder="Select dataset and variable"
            disabled={isEditing || disabled}
          />
          <ARSSelect
            dataPath={`${dataPath}/comparator`}
            id={`${dataPath.replace(/\//g, "-")}-comparator`}
            label="Comparator"
            options={comparatorOptions}
            disabled={isEditing || disabled}
          />
          <ARSText
            dataPath={`${dataPath}/value`}
            isArray
            id={`${dataPath.replace(/\//g, "-")}-value`}
            label={groupComparator ? "Values" : "Value"}
            type="textarea"
            rows={groupComparator ? 3 : 1}
            placeholder="Enter values (one per line)"
            allowCarriageReturn={true}
            disabled={isEditing || disabled}
          />
        </div>
      </Collapse>
    </div>
  );
};

WhereClauseConditionItemControl.displayName = "WhereClauseConditionItemControl";
